import React from 'react';
import Personalizer from 'personalizer';
import Conversion from 'utils/Conversion';
import DistanceUnits from 'utils/personalizer/DistanceUnits';
import DateFormatter from 'utils/date/DateFormatter';
import { MinMaxRanges } from 'AdaptiveTrainingPlans/utils/ATPUtil';
import ConversionFactors from 'utils/personalizer/ConversionFactors';
import { isPhasedTrainingPlan } from 'PhasedTrainingPlan/components/PhasedTrainingPlanSetupPage/PhasedTrainingPlanUtils';
import DateParser from 'utils/date/DateParser';
import { gcApi } from 'config/axios';
import Translate from 'components/Translate';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import TimeUtil from 'utils/date/TimeUtil';
import ActivitySpeedModelUtil from 'utils/ActivitySpeedModelUtil';
import { flatten } from 'utils/JsonUtil';
import { getSpeedFromEvent } from 'utils/eventBasedGoals/EventBasedGoalsUtil';
import i18next, { t } from 'i18next';
import { addWeeks, formatDateYYYYMMDD, startOfDay, endOfDay, addDays, getNumberOfDaysBetween } from 'utils/DateUtils';
import { createEventFromDiscoverableEvent } from 'actions/calendarActions';
import axios from 'axios';
import { convertedEventDistance } from 'utils/eventBasedGoals/EventBasedGoalsUtil';
import { DISTANCE_UNIT_MAPPER } from 'Calendar/utils/CalendarPageUtils';
import { ColorDarkuiAccent1 } from 'ui-connect/styles/default-theme';
import { EventActivityTypes } from 'constants/EventConstants';
import NumberParser from 'utils/parsers/NumberParser';

i18next.loadNamespaces(['units']);

export const hasDSWCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.adaptiveCoachingScheduleCapable;
    });
};

export const hasStpCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.strengthTrainingPlanCapable;
    });
};

export const hasATPCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.atpWorkoutCapable;
    });
};

export const hasITPCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.cyclingWorkoutCapable;
    });
};

export const hasRunningCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.runningWorkoutCapable;
    });
};

export const hasCyclingCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.cyclingWorkoutCapable;
    });
};

export const hasMultisportWorkoutCapableeDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.multisportWorkoutCapable;
    });
};

export const hasAdaptiveCoachingWorkoutSettingsCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.adaptiveCoachingWorkoutSettingsCapable;
    });
};

export const hasTriathlonCapableDevice = (devices) => {
    return (
        hasDSWCapableDevice(devices) &&
        hasMultisportWorkoutCapableeDevice(devices) &&
        hasAdaptiveCoachingWorkoutSettingsCapableDevice(devices)
    );
};

export const hasPrimaryTrainingDeviceCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.primaryTrainingCapable;
    });
};

export const canShowNewTrainingPlanSetup = (devices) => {
    return hasDSWCapableDevice(devices) || hasStpCapableDevice(devices);
};

export const isDSWStrengthCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.adaptiveCoachingStrengthCapable;
    });
};

export const Sport = {
    Running: 'running',
    Cycling: 'cycling',
    Triathlon: EventActivityTypes.triathlon
};

export const CyclingSportTypes = {
    Any: 'Any',
    RoadCycling: 'RoadCycling',
    MountainBiking: 'mountain_biking',
    GravelCycling: 'gravel_cycling'
};

// Match PlanTYpe for ATP
export const RunningDistanceTypes = {
    '5K': '5K',
    '10K': '10K',
    HalfMarathon: 'HALFMARATHON',
    Marathon: 'Marathon',
    Other: 'Other'
};

export const ATPPlanTypeIds = {
    '5K': 1,
    '10K': 2,
    HALFMARATHON: 3,
    MARATHON: 4
};

export const DistanceTypeTabs = {
    Distance: 'Distance',
    Time: 'Time'
};

export const GoalTypes = {
    RunWalk: 'RUN_WALK', // For 5K Only
    Complete: 'COMPLETION',
    CompleteWithGoal: 'CompleteWithGoal',
    Pace: 'PACE',
    Run: 'RUN',
    LegTimeGoals: 'LegTimeGoals',
    Finish: 'Finish'
};

export const TRAINING_STATUS_KEYS = {
    Scheduled: 'Scheduled',
    Completed: 'Completed',
    Draft: 'Draft',
    Published: 'Published',
    Expired: 'Expired',
    Paused: 'Paused'
};

export const SensorType = {
    HeartRateBased: 'HeartRateBased',
    PowerBased: 'PowerBased'
};

export const TrainingHours = {
    ZeroToFour: 'ZeroToFour',
    FiveToNine: 'FiveToNine',
    TenPlus: 'TenPlus'
};

export const SwimTypes = {
    Novice: 'NOVICE',
    Intermediate: 'INTERMEDIATE',
    Experienced: 'EXPERIENCED',
    Expert: 'EXPERT'
};

export const END_OF_PLAN_TYPES = {
    RACE: 'race',
    MILESTONE: 'milestone'
};

export const getRunningDistance = (type) => {
    let distanceInMeters;
    switch (type) {
        case RunningDistanceTypes['5K']:
            distanceInMeters = 5000;
            break;
        case RunningDistanceTypes['10K']:
            distanceInMeters = 10000;
            break;
        case RunningDistanceTypes.HalfMarathon:
            distanceInMeters = 21097.5;
            break;
        case RunningDistanceTypes.Marathon:
            distanceInMeters = 42195;
            break;
        default:
            distanceInMeters = 0;
    }
    return distanceInMeters;
};

export const convertDistanceToMeters = (distance, unitType) => {
    let meters;
    if (unitType === DistanceUnits.mile) {
        meters = Conversion.convertDistance(distance, DistanceUnits.mile, DistanceUnits.meter);
    } else if (unitType === DistanceUnits.kilometer) {
        meters = Conversion.convertDistance(distance, DistanceUnits.kilometer, DistanceUnits.meter);
    }
    return meters;
};

export const convertDistanceToUnit = (distanceInMeters, unitType) => {
    let value;
    if (unitType === DistanceUnits.mile) {
        value = Conversion.convertDistance(distanceInMeters, DistanceUnits.meter, DistanceUnits.mile);
    } else if (unitType === DistanceUnits.kilometer) {
        value = Conversion.convertDistance(distanceInMeters, DistanceUnits.meter, DistanceUnits.kilometer);
    }
    return value;
};

export const isMetric = () => {
    return Personalizer.isMetricMeasurementSystem();
};

export const EventTrainingPlanTypeIds = {
    STATIC: 'STATIC',
    ITP: 'ITP',
    FBT_ADAPTIVE: 'FBT_ADAPTIVE',
    COACH_ATP: 'COACH_ATP',
    STRENGTH: 'STRENGTH'
};

export const CoachPlansTrainingSubTypeKeys = [
    'GarminRunningCoachEventBased',
    'GarminRunningCoachOpenLoop',
    'GarminCyclingCoachEventBased',
    'GarminCyclingCoachOpenLoop',
    'GarminTriathlonCoachEventBased',
    'GarminTriathlonCoachOpenLoop'
];

export const getPlanName = (id, planTypes, t) => {
    for (let i = 0, len = planTypes.length; i < len; i += 1) {
        if (planTypes[i] && planTypes[i].workoutPlanTypeId === id) {
            return planTypes[i].name + ' ' + t('training_plans:training_plan_plan_label');
        }
    }
    //default: Adaptive Training Plan
    return t('training_plans:atp_training_plan_label');
};

export const getDateString = (startDate, endDate, printPreview = false) => {
    const start = DateParser.parseISO(startDate);
    const end = DateParser.parseISO(endDate);
    const now = new Date();
    const showYear = start.getFullYear() !== end.getFullYear() || now.getFullYear() !== end.getFullYear();
    if (printPreview) {
        return `${DateFormatter.formatISODate(start)} - ${DateFormatter.formatISODate(end)}`;
    } else {
        return `${DateFormatter.formatMonthDay(start)} - ${
            showYear ? DateFormatter.formatMonthDayYear(end) : DateFormatter.formatMonthDay(end)
        }`;
    }
};

export const is5K = (distanceInMeters) => {
    let minMax = MinMaxRanges['5K'];
    return distanceInMeters >= minMax.startDistance && distanceInMeters <= minMax.endDistance;
};

export const is10K = (distanceInMeters) => {
    let minMax = MinMaxRanges['10K'];
    return distanceInMeters >= minMax.startDistance && distanceInMeters <= minMax.endDistance;
};

export const isHalf = (distanceInMeters) => {
    let minMax = MinMaxRanges['HALFMARATHON'];
    return distanceInMeters >= minMax.startDistance && distanceInMeters <= minMax.endDistance;
};

export const isFull = (distanceInMeters) => {
    let minMax = MinMaxRanges['MARATHON'];
    return distanceInMeters >= minMax.startDistance && distanceInMeters <= minMax.endDistance;
};

export const getATPGoal = (planTypeId, goalType) => {
    let atpGoal = goalType;
    if (planTypeId === 1 && goalType === GoalTypes.Complete) {
        atpGoal = 'RUN';
    } else if (goalType === GoalTypes.Complete) {
        atpGoal = 'COMPLETION';
    }
    if (goalType === GoalTypes.CompleteWithGoal) {
        atpGoal = 'PACE';
    }
    return atpGoal;
};

export const isRunningSport = (pageState) => {
    return (
        pageState.sport === Sport.Running ||
        pageState.selectedEvent?.parentTypeKey === Sport.Running ||
        !!pageState.runningDistanceType ||
        !!pageState.runningDistance
    );
};

export const isCyclingSport = (pageState) => {
    return pageState.sport === Sport.Cycling || pageState.selectedEvent?.parentTypeKey === Sport.Cycling;
};

export const isTriathlonSport = (pageState) => {
    return pageState.sport === Sport.Triathlon || pageState.selectedEvent?.parentTypeKey === Sport.Triathlon;
};

export const isAdaptiveCoachPlan = (pageState) => {
    let isAdaptive = false;
    let planTypeId;
    let distanceInMeters = 0;
    let goal;
    let planType;
    if (pageState.sport === Sport.Running) {
        if (pageState.runningDistanceType) {
            isAdaptive =
                pageState.runningDistanceType === RunningDistanceTypes['5K'] ||
                pageState.runningDistanceType === RunningDistanceTypes['10K'] ||
                pageState.runningDistanceType === RunningDistanceTypes.HalfMarathon;
            if (isAdaptive) {
                planTypeId = ATPPlanTypeIds[pageState.runningDistanceType];
                planType = pageState.runningDistanceType;
            }
            if (pageState.runningDistanceType === RunningDistanceTypes.Other) {
                distanceInMeters = Conversion.convertDistance(
                    pageState.runningDistance,
                    isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile,
                    DistanceUnits.meter
                );
            }
        }
    }
    if (pageState.selectedEvent && pageState.selectedEvent?.completionTarget?.unitType === 'distance') {
        distanceInMeters = Conversion.convertDistance(
            pageState.selectedEvent.completionTarget.value,
            pageState.selectedEvent.completionTarget.unit,
            DistanceUnits.meter
        );
    }
    if (!isAdaptive && distanceInMeters > 0) {
        isAdaptive = is5K(distanceInMeters);
        if (isAdaptive) {
            planTypeId = ATPPlanTypeIds['5K'];
            planType = '5K';
        } else {
            isAdaptive = is10K(distanceInMeters);
            if (isAdaptive) {
                planTypeId = ATPPlanTypeIds['10K'];
                planType = '10K';
            }
        }
        if (!isAdaptive) {
            isAdaptive = isHalf(distanceInMeters);
            if (isAdaptive) {
                planTypeId = ATPPlanTypeIds['HALFMARATHON'];
                planType = 'HALFMARATHON';
            }
        }
    }

    if (isAdaptive) {
        goal = getATPGoal(planTypeId, pageState.goal.goalType);
    }
    return {
        isAdaptive,
        planTypeId,
        planType,
        goal
    };
};

export const calculateWeeklyMileage = (recentWeeklyAvgValue) => {
    if (!recentWeeklyAvgValue) {
        return 0;
    }
    const val = Conversion.convertDistance(
        recentWeeklyAvgValue,
        DistanceUnits.meter,
        isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile
    );
    return val;
};

export const calculateAveragePaceInSeconds = (avgSpeed) => {
    if (!avgSpeed) {
        return 0;
    }
    let seconds;
    if (isMetric()) {
        // convert to seconds per kilometer
        seconds = (1 / avgSpeed) * ConversionFactors.MpsToKilometerPerSecond;
    } else {
        // convert to seconds per mile
        seconds = (1 / avgSpeed) * ConversionFactors.MpsToMilesPerSecond;
    }
    return Math.round(seconds);
};

export const isStaticPlan = (plan) => {
    return (
        (plan?.trainingPlanCategory === 'STATIC' && plan?.trainingType?.typeKey !== 'Strength') ||
        (plan?.trainingPlanCategory === 'ITP' && !isPhasedTrainingPlan(plan))
    );
};

export const quitAdaptivePlan = (athletePlanId) => {
    return gcApi.post(`/atp/athlete/quit?athletePlanId=${athletePlanId}`);
};

export const SetupPageState = 'SetupPageState';
export const ImprovePageState = 'ImprovePageState';

export const getDistanceOutofBoundsErrorMessage = (min, max, unit) => {
    let unitKey = '';

    switch (unit) {
        case DistanceUnits.mile:
            unitKey = 'label_miles';
            break;
        case DistanceUnits.kilometer:
            unitKey = 'label_kilometers';
            break;
        case DistanceUnits.meter:
            unitKey = 'label_meters';
            break;
        case DistanceUnits.yard:
            unitKey = 'label_yards';
            break;
    }

    return (
        <Translate ns={['units']}>
            {(t) => (
                <Translate
                    ns="dsw"
                    content="dsw_invalid_distance"
                    params={{ 0: min, 1: max, 2: t(`units:${unitKey}`) }}
                />
            )}
        </Translate>
    );
};

export const MaxDistanceNonDSW = {
    [Sport.Cycling]: {
        Min: {
            [DistanceUnits.mile]: 10,
            [DistanceUnits.kilometer]: 16
        },
        Max: {
            [DistanceUnits.mile]: 125,
            [DistanceUnits.kilometer]: 201
        }
    }
};

export const ITP_SubTypes = {
    TimeTrial: 'TimeTrial',
    GranFondo: 'GranFondo',
    HalfCentury: 'HalfCentury', // Metric
    FullCentury: 'FullCentury',
    Crit: 'Crit', // Race
    MountainBiking: 'MountainBiking'
};
// All distances are in miles
export const DistanceRange1 = {
    low: 10,
    high: 49
};

export const DistanceRange2 = {
    low: 50,
    high: 54
};

export const DistanceRange3 = {
    low: 55,
    high: 69
};

export const DistanceRange4 = {
    low: 70,
    high: 109
};

export const DistanceRange5 = {
    low: 110,
    high: 125
};

export const maxDuration = 172800; // 48 Hours

const isDurationWithinRange = (duration) => {
    // Duration between 30 minutes and 2 hours in seconds
    return duration >= 1800 && duration <= 7200;
};

export const CyclingPlansFilter = {
    [CyclingSportTypes.Any]: {
        getPlansByDistance: (distance) => {
            let plans = [];
            if (distance >= DistanceRange1.low && distance <= DistanceRange1.high) {
                plans.push(ITP_SubTypes.Crit);
            } else if (distance >= DistanceRange2.low && distance <= DistanceRange2.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.HalfCentury);
                plans.push(ITP_SubTypes.Crit);
            } else if (distance >= DistanceRange3.low && distance <= DistanceRange3.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.HalfCentury);
            } else if (distance >= DistanceRange4.low && distance <= DistanceRange4.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.FullCentury);
            } else if (distance >= DistanceRange5.low && distance <= DistanceRange5.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.FullCentury);
            }
            return plans;
        },
        getPlansByDuration: (duration) => {
            let plans = [];
            if (isDurationWithinRange(duration)) {
                plans.push(ITP_SubTypes.Crit);
            }
            return plans;
        }
    },
    [CyclingSportTypes.RoadCycling]: {
        getPlansByDistance: (distance) => {
            let plans = [];
            if (distance >= DistanceRange1.low && distance <= DistanceRange1.high) {
                plans.push(ITP_SubTypes.Crit);
                plans.push(ITP_SubTypes.TimeTrial);
            } else if (distance >= DistanceRange2.low && distance <= DistanceRange2.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.HalfCentury);
                plans.push(ITP_SubTypes.TimeTrial);
                plans.push(ITP_SubTypes.Crit);
            } else if (distance >= DistanceRange3.low && distance <= DistanceRange3.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.HalfCentury);
                plans.push(ITP_SubTypes.TimeTrial);
            } else if (distance >= DistanceRange4.low && distance <= DistanceRange4.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.FullCentury);
                plans.push(ITP_SubTypes.TimeTrial);
            } else if (distance >= DistanceRange5.low && distance <= DistanceRange5.high) {
                plans.push(ITP_SubTypes.Crit);
                plans.push(ITP_SubTypes.TimeTrial);
            }
            return plans;
        },
        getPlansByDuration: (duration) => {
            let plans = [];
            if (isDurationWithinRange(duration)) {
                plans.push(ITP_SubTypes.Crit);
            }
            return plans;
        }
    },
    [CyclingSportTypes.GravelCycling]: {
        getPlansByDistance: (distance) => {
            let plans = [];
            if (distance >= DistanceRange1.low && distance <= DistanceRange1.high) {
                plans.push(ITP_SubTypes.Crit);
                plans.push(ITP_SubTypes.TimeTrial);
            } else if (distance >= DistanceRange2.low && distance <= DistanceRange2.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.HalfCentury);
                plans.push(ITP_SubTypes.Crit);
                plans.push(ITP_SubTypes.TimeTrial);
            } else if (distance >= DistanceRange3.low && distance <= DistanceRange3.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.HalfCentury);
                plans.push(ITP_SubTypes.TimeTrial);
            } else if (distance >= DistanceRange4.low && distance <= DistanceRange4.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.FullCentury);
                plans.push(ITP_SubTypes.TimeTrial);
            } else if (distance >= DistanceRange5.low && distance <= DistanceRange5.high) {
                plans.push(ITP_SubTypes.GranFondo);
                plans.push(ITP_SubTypes.FullCentury);
            }
            return plans;
        },
        getPlansByDuration: (duration) => {
            let plans = [];
            if (isDurationWithinRange(duration)) {
                plans.push(ITP_SubTypes.Crit);
            }
            return plans;
        }
    },
    [CyclingSportTypes.MountainBiking]: {
        getPlansByDistance: (distance) => {
            let plans = [];
            if (distance >= DistanceRange1.low) {
                plans.push(ITP_SubTypes.MountainBiking);
            }
            return plans;
        },
        getPlansByDuration: (duration) => {
            let plans = [ITP_SubTypes.MountainBiking];
            return plans;
        }
    }
};

export const pushEvent = ({ category = 'DSW Events', action, label }) => {
    AnalyticsUtil.push({
        event: 'track_event',
        category: category,
        action: action,
        label: label
    });
};

export const atpPrePlanMileage = {
    Statute: [
        {
            distanceMin: 0,
            distanceMax: 0,
            prePlanWeeklyMileage: 0
        },
        {
            distanceMin: 1,
            distanceMax: 5,
            prePlanWeeklyMileage: 5
        },
        {
            distanceMin: 6,
            distanceMax: 10,
            prePlanWeeklyMileage: 10
        },
        {
            distanceMin: 11,
            distanceMax: 15,
            prePlanWeeklyMileage: 15
        },
        {
            distanceMin: 16,
            distanceMax: 20,
            prePlanWeeklyMileage: 20
        },
        {
            distanceMin: 21,
            distanceMax: 25,
            prePlanWeeklyMileage: 25
        },
        {
            distanceMin: 25,
            distanceMax: 25,
            prePlanWeeklyMileage: 25
        }
    ],
    Metric: [
        {
            distanceMin: 0,
            distanceMax: 0,
            prePlanWeeklyMileage: 0
        },
        {
            distanceMin: 1,
            distanceMax: 8,
            prePlanWeeklyMileage: 5
        },
        {
            distanceMin: 9,
            distanceMax: 16,
            prePlanWeeklyMileage: 10
        },
        {
            distanceMin: 17,
            distanceMax: 24,
            prePlanWeeklyMileage: 15
        },
        {
            distanceMin: 25,
            distanceMax: 32,
            prePlanWeeklyMileage: 20
        },
        {
            distanceMin: 33,
            distanceMax: 40,
            prePlanWeeklyMileage: 25
        },
        {
            distanceMin: 40,
            distanceMax: 40,
            prePlanWeeklyMileage: 25
        }
    ]
};

export const parseWeeklyDistance = (distance) => {
    if (('' + distance).indexOf('-') > -1) {
        return +distance.substring(0, distance.indexOf('-'));
    } else {
        return distance;
    }
};

export const getAdaptivePlanTypeId = (runningDistanceType, runningDistance) => {
    let planTypeId;
    let distanceInMeters = 0;
    if (runningDistance) {
        distanceInMeters = Conversion.convertDistance(
            runningDistance,
            isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile,
            DistanceUnits.meter
        );
    }

    if (runningDistanceType === RunningDistanceTypes['5K'] || is5K(distanceInMeters)) {
        planTypeId = ATPPlanTypeIds['5K'];
    } else if (runningDistanceType === RunningDistanceTypes['10K'] || is10K(distanceInMeters)) {
        planTypeId = ATPPlanTypeIds['10K'];
    } else if (runningDistanceType === RunningDistanceTypes['HalfMarathon'] || isHalf(distanceInMeters)) {
        planTypeId = ATPPlanTypeIds['HALFMARATHON'];
    }

    return planTypeId;
};

export const getRunningType = (adaptivePlanTypeId) => {
    if (adaptivePlanTypeId === 1) {
        return RunningDistanceTypes['5K'];
    } else if (adaptivePlanTypeId === 2) {
        return RunningDistanceTypes['10K'];
    } else if (adaptivePlanTypeId === 3) {
        return RunningDistanceTypes['HalfMarathon'];
    } else {
        return null;
    }
};

export const calculateMinMax = (distance) => {
    let distanceInMeters = Math.round(
        Conversion.convertDistance(
            distance,
            isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile,
            DistanceUnits.meter
        )
    );
    let percent = (distanceInMeters / 100) * 5;
    const lowEnd = Math.floor(distanceInMeters - percent);
    const highEnd = Math.ceil(distanceInMeters + percent);
    return {
        min: lowEnd,
        max: highEnd
    };
};

export const getMinMaxRunningDistance = (runningDistanceType, runningDistance) => {
    let distanceInMeters = 0;
    let minMax;

    if (runningDistance) {
        distanceInMeters = Conversion.convertDistance(
            runningDistance,
            isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile,
            DistanceUnits.meter
        );
    }

    if (runningDistanceType === RunningDistanceTypes['5K'] || is5K(distanceInMeters)) {
        minMax = {
            min: MinMaxRanges['5K'].startDistance,
            max: MinMaxRanges['5K'].endDistance
        };
    } else if (runningDistanceType === RunningDistanceTypes['10K'] || is10K(distanceInMeters)) {
        minMax = {
            min: MinMaxRanges['10K'].startDistance,
            max: MinMaxRanges['10K'].endDistance
        };
    } else if (runningDistanceType === RunningDistanceTypes['HalfMarathon'] || isHalf(distanceInMeters)) {
        minMax = {
            min: MinMaxRanges['HALFMARATHON'].startDistance,
            max: MinMaxRanges['HALFMARATHON'].endDistance
        };
    } else if (isFull(distanceInMeters)) {
        minMax = {
            min: MinMaxRanges['MARATHON'].startDistance,
            max: MinMaxRanges['MARATHON'].endDistance
        };
    } else {
        minMax = calculateMinMax(runningDistance);
    }

    return minMax;
};

export const converDistanceToMetersPerUserUnit = (distance) => {
    return convertDistanceToMeters(distance, isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile);
};

export const getDistanceInMetersByRunningDistanceType = (runningDistanceType) => {
    let distance = 0;
    switch (runningDistanceType) {
        case RunningDistanceTypes['5K']:
            distance = 5000;
            break;
        case RunningDistanceTypes['10K']:
            distance = 10000;
            break;
        case RunningDistanceTypes['HalfMarathon']:
            distance = 21100;
            break;
        case RunningDistanceTypes['Marathon']:
            distance = 42195;
            break;
    }
    return distance;
};

export const getDefaultEventName = (t, pageState) => {
    const coachPlan = isAdaptiveCoachPlan(pageState);
    const isRun = isRunningSport(pageState);
    const isTriathlon = isTriathlonSport(pageState);
    let distance;
    if (coachPlan.isAdaptive) {
        let distance;
        switch (coachPlan.planType) {
            case '5K':
                distance = t('training_plans:active_5k_race');
                break;
            case '10K':
                distance = t('training_plans:active_10k_race');
                break;
            case RunningDistanceTypes.HalfMarathon:
                distance = t('training_plans:active_half_marathon_race');
                break;
        }
        if (pageState?.planType?.type === EventTrainingPlanTypeIds.COACH_ATP) {
            return t('dsw:adaptive_plan_header', { 0: distance, 1: pageState.planType.coach.coachInfo.firstName });
        } else {
            // Garmin Run Coach
            return t('dsw:garmin_coach_plan_header', { 0: distance, 1: t('dsw:run_label') });
        }
    } else if (isRun) {
        // Garmin Run Coach
        if (pageState.distanceOrTimeSet === DistanceTypeTabs.Time) {
            distance = TimeUtil.secondsToHoursMinutesAndSeconds(pageState.distanceTime);
        } else if (pageState.runningDistanceType) {
            if (pageState.runningDistanceType === RunningDistanceTypes.Marathon) {
                distance = t('training_plans:active_marathon_race');
            } else if (pageState.runningDistance) {
                let distanceInMeters = convertDistanceToMeters(
                    pageState.runningDistance,
                    isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile
                );
                if (isFull(distanceInMeters)) {
                    distance = t('training_plans:active_marathon_race');
                } else {
                    let unit = t(`common:${isMetric() ? 'kilometers_abbrev' : 'miles_abbrev'}`);
                    let personalized = Personalizer.personalizeDistance(distanceInMeters, 'running');
                    distance = `${personalized} ${unit}`;
                }
            }
        }
        return t('dsw:garmin_coach_plan_header', { 0: distance, 1: t('dsw:run_label') });
    } else if (isTriathlon) {
        const distanceInMeters = getTriathlonDistance(pageState.triathlonDistance);
        let unit = t(`common:${isMetric() ? 'kilometers_abbrev' : 'miles_abbrev'}`);
        let personalized = Personalizer.personalizeDistance(distanceInMeters, 'running');
        distance = `${personalized} ${unit}`;
        return t('dsw:garmin_coach_plan_header', { 0: distance, 1: t('dsw:triathlon_upper') });
    } else {
        if (pageState.planType.type === EventTrainingPlanTypeIds.ITP) {
            return pageState.planType.coach.planName;
        } else {
            // Garmin Cycling Coach
            if (pageState.cyclingDistance) {
                let distanceInMeters = convertDistanceToMeters(
                    pageState.cyclingDistance,
                    isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile
                );
                let unit = t(`common:${isMetric() ? 'kilometers_abbrev' : 'miles_abbrev'}`);
                let personalized = Personalizer.personalizeDistance(distanceInMeters, 'cycling');
                distance = `${personalized} ${unit}`;
                return t('dsw:garmin_coach_plan_header', { 0: distance, 1: t('dsw:cycling_upper') });
            } else {
                // Timebased
                return pageState.planType.coach.planName;
            }
        }
    }
};

export const getDefaultPlanName = (t, pageState) => {
    const coachPlan = isAdaptiveCoachPlan(pageState);
    const isRun = isRunningSport(pageState);
    const isTriathlon = isTriathlonSport(pageState);
    let eventName = pageState.userEvent ? pageState.userEvent?.eventName : pageState.selectedEvent?.eventName;
    if (coachPlan.isAdaptive) {
        let distance;
        switch (coachPlan.planType) {
            case '5K':
                distance = t('training_plans:active_5k_race');
                break;
            case '10K':
                distance = t('training_plans:active_10k_race');
                break;
            case 'HalfMarathon':
                distance = t('training_plans:active_half_marathon_race');
                break;
        }
        if (pageState?.planType?.type === EventTrainingPlanTypeIds.COACH_ATP) {
            return t('dsw:adaptive_plan_header', { 0: distance, 1: pageState.planType.coach.coachInfo.firstName });
        } else {
            if (pageState.mileStone) {
                // Garmin Run Coach
                return eventName;
            } else {
                // Garmin Run Coach
                return t('dsw:itp_plan_header', { 0: eventName, interpolation: { escapeValue: false } });
            }
        }
    } else if (isRun) {
        // Garmin Run Coach
        if (pageState.mileStone) {
            return eventName;
        } else {
            // Garmin Run Coach
            return t('dsw:itp_plan_header', { 0: eventName, interpolation: { escapeValue: false } });
        }
    } else if (isTriathlon) {
        if (pageState.mileStone) {
            return eventName;
        } else {
            return t('dsw:itp_plan_header', { 0: eventName, interpolation: { escapeValue: false } });
        }
    } else {
        if (pageState?.planType?.type === EventTrainingPlanTypeIds.ITP) {
            if (pageState.mileStone) {
                return eventName;
            } else {
                return pageState.planType.coach.planName;
            }
        } else {
            // Garmin Cycling Coach
            if (pageState.mileStone) {
                return eventName;
            } else {
                // Garmin Cycling Coach
                return t('dsw:itp_plan_header', { 0: eventName, interpolation: { escapeValue: false } });
            }
        }
    }
};

export const getDistanceInMeters = (pageState, forGoal = false) => {
    if (pageState.cyclingDistance) {
        return converDistanceToMetersPerUserUnit(pageState.cyclingDistance);
    } else if (pageState.runningDistance) {
        return converDistanceToMetersPerUserUnit(pageState.runningDistance);
    } else if (pageState.runningDistanceType) {
        let distanceInMeters = getRunningDistance(pageState.runningDistanceType);
        if (forGoal) {
            // Need to convert to personalized and then back to meters to get rid of some rounding differences
            // With goals
            let converted = Personalizer.personalizeDistance(distanceInMeters, 'running');
            distanceInMeters = converDistanceToMetersPerUserUnit(+converted);
        }
        return distanceInMeters;
    }
};

export const calculatePace = (timeInSecs, distanceInMeters) => {
    let pace = Personalizer.personalizePace(distanceInMeters / timeInSecs);
    return pace;
};

export const OrderedDays = [
    {
        name: 'SUNDAY',
        propKey: 'day.0',
        short: 'day.0.short'
    },
    {
        name: 'MONDAY',
        propKey: 'day.1',
        short: 'day.1.short'
    },
    {
        name: 'TUESDAY',
        propKey: 'day.2',
        short: 'day.2.short'
    },
    {
        name: 'WEDNESDAY',
        propKey: 'day.3',
        short: 'day.3.short'
    },
    {
        name: 'THURSDAY',
        propKey: 'day.4',
        short: 'day.4.short'
    },
    {
        name: 'FRIDAY',
        propKey: 'day.5',
        short: 'day.5.short'
    },
    {
        name: 'SATURDAY',
        propKey: 'day.6',
        short: 'day.6.short'
    }
];

export const sortTrainingDays = (days, firstDayOfWeek) => {
    if (days.length > 1) {
        let ordered = [...OrderedDays];
        let index = ordered.findIndex((day) => {
            return firstDayOfWeek.toLowerCase() === day.name.toLowerCase();
        });
        if (index !== 0) {
            let temp = ordered.splice(index);
            ordered = temp.concat(ordered);
        }
        let orderdArray = [];
        ordered.forEach((d) => {
            let found = days.find((day) => {
                return day === d.name;
            });
            if (found) {
                orderdArray.push(d.name);
            }
        });
        return orderdArray;
    } else {
        return days;
    }
};

export const DAY_TRANSLATIONS_MAP = {
    MONDAY: 'day_1_short',
    TUESDAY: 'day_2_short',
    WEDNESDAY: 'day_3_short',
    THURSDAY: 'day_4_short',
    FRIDAY: 'day_5_short',
    SATURDAY: 'day_6_short',
    SUNDAY: 'day_7_short'
};

export const DAY_TRANSLATIONS_LONG_MAP = {
    MONDAY: 'day.1',
    TUESDAY: 'day.2',
    WEDNESDAY: 'day.3',
    THURSDAY: 'day.4',
    FRIDAY: 'day.5',
    SATURDAY: 'day.6',
    SUNDAY: 'day.0'
};

export const getGoal = (isDistanceBasedEvent, event, isCustomFormatSeconds = false) => {
    if (isDistanceBasedEvent) {
        // if distance based event, show custom goal in time
        if (isCustomFormatSeconds) {
            return formatSeconds(event?.eventCustomization?.customGoal.value);
        } else {
            return TimeUtil.formatSeconds(event?.eventCustomization?.customGoal.value);
        }
    } else {
        // if time based event, show distance goal
        return `${Personalizer.formatDistance(event?.eventCustomization?.customGoal.value, event?.eventType)} ${t(
            `units:${DISTANCE_UNIT_MAPPER[event.eventCustomization.customGoal.unit]}`
        )}`;
    }
};

export const getGoalFromActivity = (isDistanceBasedEvent, activity) => {
    if (isDistanceBasedEvent) {
        // if distance based event, show custom goal in time
        return TimeUtil.formatSeconds(activity?.summaryDTO?.duration);
    } else {
        // if time based event, show distance goal
        return `${Personalizer.formatDistance(activity?.summaryDTO?.distance, activity?.activityTypeDTO?.typeKey)} ${t(
            `units:${Personalizer.getDistanceUnitKey(activity?.activityTypeDTO?.typeKey)}`
        )}`;
    }
};

export const getPace = (event) => {
    let copy = { ...event };
    if (copy.completionTarget?.unitType === 'distance') {
        // Some shareable events are in meters. We need to convert to miles or kilometers
        // So that the pace will match what we display on the goals
        let converted = convertedEventDistance(copy);
        copy.completionTarget.unit = converted.unit;
        copy.completionTarget.value = Math.round(converted.distance * 100) / 100;
    }
    const speedOrPace = getSpeedFromEvent(copy);
    const speedOrPaceLabel = t(`units:${Personalizer.getSpeedTypeKey(copy?.eventType)}`);
    return `${speedOrPace} ${speedOrPaceLabel}`;
};

export const getPaceFromActivity = (activity) => {
    if (!activity) return;

    let flattenedActivity = flatten(activity); // speed model expects flat model
    flattenedActivity.activityType = activity.activityTypeDTO; // have to manually add back the activity type

    const speedModel = ActivitySpeedModelUtil.getSpeedModelforActivity(flattenedActivity);

    return `${speedModel.avgSpeedOrPace} ${speedModel.avgSpeedOrPaceUnit}`;
};
export const getMinMaxDateRangesByPlanTypes = ({ planType, minWeeksPerPlan, maxWeeksPerPlan }) => {
    let startDate = startOfDay(new Date());
    let endDate = endOfDay(new Date());
    let defaultDate;

    if (planType === EventTrainingPlanTypeIds.COACH_ATP && minWeeksPerPlan && maxWeeksPerPlan) {
        startDate.setDate(startDate.getDate() + minWeeksPerPlan * 7);
        endDate.setDate(endDate.getDate() + maxWeeksPerPlan * 7);
        let start = startOfDay(startDate);
        // Is the start Saturday
        if (start.getDay() !== 6) {
            while (start < endDate) {
                if (start.getDay() === 6) {
                    break;
                } else {
                    start = addDays(start, 1);
                }
            }
            // Set default date, the first Saturday
            defaultDate = formatDateYYYYMMDD(start);
        } else {
            // Set default date, the first Saturday
            defaultDate = formatDateYYYYMMDD(start);
        }
    } else if (planType === EventTrainingPlanTypeIds.FBT_ADAPTIVE) {
        endDate = addWeeks(startDate, 52);
        startDate = addWeeks(startDate, 6);
        // Set default date 16 weeks from today
        defaultDate = formatDateYYYYMMDD(startOfDay(addWeeks(new Date(), 16)));
    } else if (planType === EventTrainingPlanTypeIds.ITP) {
        // PhasedTrainingPlanSetupRaceDayStep.jsx
        startDate = addWeeks(startDate, 6);
        endDate = startOfDay(addWeeks(endDate, 30));
        // Set default date 22 weeks from today
        defaultDate = formatDateYYYYMMDD(startOfDay(addWeeks(new Date(), 22)));
    }

    let min = formatDateYYYYMMDD(startDate);
    let max = formatDateYYYYMMDD(endDate);

    return {
        minDate: min,
        maxDate: max,
        defaultDate: defaultDate
    };
};

export const getTrainingPlanLevel = (levels, weeklyTrainingTime) => {
    let weeklyTrainingTimeJSON = JSON.parse(weeklyTrainingTime);
    let min = weeklyTrainingTimeJSON.workoutsPerWeekMin;
    let max = weeklyTrainingTimeJSON.workoutsPerWeekMax;
    let trainingPlanLevel;
    levels.forEach((level) => {
        if (level.workoutsPerWeekMin === min && level.workoutsPerWeekMax === max) {
            trainingPlanLevel = level.levelKey;
        }
    });
    return trainingPlanLevel;
};

const dayMap = {
    MONDAY: 1,
    TUESDAY: 2,
    WEDNESDAY: 3,
    THURSDAY: 4,
    FRIDAY: 5,
    SATURDAY: 6,
    SUNDAY: 7
};

export const createITPPlan = async (pageState, planName, locale) => {
    const daysAvailable = pageState.trainingDaysPage.availableTrainingDays.map((day) => {
        return dayMap[day];
    });
    const longDayRide = dayMap[pageState.trainingDaysPage.preferredLongTrainingDays[0]];
    const trainingPlanLevel = getTrainingPlanLevel(
        pageState.planType.coach.planDetail.levels,
        pageState.trainingDaysPage.weeklyTrainingTime
    );
    let eventDate;
    if (pageState.userEvent?.date) {
        eventDate = pageState.userEvent.date;
    } else if (pageState.userEvent?.eventDate) {
        eventDate = pageState.userEvent.eventDate;
    } else if (pageState.selectedEvent?.date) {
        eventDate = pageState.selectedEvent.date;
    } else if (pageState?.trainingDaysPage?.goalEndDate) {
        eventDate = pageState.trainingDaysPage.goalEndDate; // An goalEndDate is set during the non-DSW ITP setup flow
    }
    const plan = {
        hasPowerMeter: pageState.trainingDaysPage.workoutTarget === SensorType.PowerBased,
        hasHRMonitor: pageState.trainingDaysPage.workoutTarget === SensorType.HeartRateBased,
        daysAvailable: daysAvailable,
        longRideDay: longDayRide,
        eventType: 'Cycling',
        eventSubType: pageState.planType.coach.subType,
        localKey: locale,
        trainingPlanLevel: trainingPlanLevel,
        startDate: DateFormatter.formatISODate(new Date()),
        eventDate: eventDate,
        trainingPlanName: planName
    };

    const { data } = await gcApi.post('/trainingplan-service/trainingplan/phased/cycling/trainingplan', plan);

    return data;
};

export const saveConversionEvent = async (event) => {
    // The event is from the event search
    let conversionEvent = {
        eventName: event.eventName,
        date: event.eventDate,
        eventStartTime: event.eventStartTime,
        url: event.eventUrl,
        registrationUrl: event.registrationUrl,
        completionTarget:
            event.completionTargets && Array.isArray(event.completionTargets) ? event.completionTargets[0] : null,
        locationStartPoint: event.locationStartPoint,
        eventType: event.eventType,
        administrativeArea: event.administrativeArea,
        eventsProvider: event.provider,
        eventRef: event.eventRef,
        garminEventUuid: event.garminEventUuid,
        hasCourse: event.hasCourse,
        isOfficial: event.isOfficial,
        sig: event.sig,
        verifiedStatus: event.verifiedStatus
    };

    if (event.eventType === Sport.Triathlon) {
        conversionEvent.completionTargetLegs = event.completionTargetLegs;
    }

    return createEventFromDiscoverableEvent(conversionEvent);
};

export const makeATPObject = (pageState, trainingPlanEvent) => {
    const coachPlan = isAdaptiveCoachPlan(pageState);
    let planType = coachPlan.planType;
    let planTypeId = ATPPlanTypeIds[coachPlan.planType];
    const coach = pageState.planType.coach;
    let prePlanTrainingPace = coach.prePlan.prePlanTrainingPace;
    let prePlanWeeklyMileage = coach.prePlan.prePlanWeeklyMileage;
    let selectedPaceGoalTime = 0;
    let goalType = pageState.goal.goalType;
    if (goalType === GoalTypes.CompleteWithGoal) {
        goalType = GoalTypes.Pace;
        selectedPaceGoalTime = Math.round(+pageState.goal.goal.value);
    } else if (planType === '5K' && goalType === GoalTypes.Complete) {
        goalType = GoalTypes.Run;
    }
    return {
        planTypeId: planTypeId,
        selectedGoal: goalType,
        prePlanWeeklyMileage: prePlanWeeklyMileage,
        prePlanTrainingPace: prePlanTrainingPace,
        selectedPaceGoalTime: selectedPaceGoalTime,
        longRunDay: pageState.trainingDaysPage.preferredLongTrainingDays[0],
        selectedRace: {
            raceDate: trainingPlanEvent.date,
            raceTitle: trainingPlanEvent.eventName,
            raceRegistrationUrl: trainingPlanEvent.registrationUrl,
            raceEventId: trainingPlanEvent.id,
            raceEventUuid: trainingPlanEvent.shareableEventUuid
        },
        selectedWorkoutDays: pageState.trainingDaysPage.availableTrainingDays,
        selectedWorkoutPlanId: coach.workoutPlanId,
        selectedWorkoutsPerWeek: pageState.trainingDaysPage.workoutsPerWeek,
        planType: planType
    };
};

export const sortLongTrainingDayForNonDSW = (preferredLongTrainingDays) => {
    let copy = [...preferredLongTrainingDays];
    if (copy.length > 1) {
        copy.sort((d, d2) => {
            return dayMap[d2] - dayMap[d];
        });
    }
    return copy;
};

const daysOfWeek = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
const daysOfWeekSundayFirst = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
const daysOfWeekSaturdayFirst = ['SATURDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

const SUNDAY = 'sunday';
const SATURDAY = 'saturday';

export const onlyWeekdaysSelected = (days) => {
    return (
        days.length === 5 &&
        days.includes('MONDAY') &&
        days.includes('TUESDAY') &&
        days.includes('WEDNESDAY') &&
        days.includes('THURSDAY') &&
        days.includes('FRIDAY')
    );
};

export const onlyWeekendsSelected = (days) => {
    return days.length === 2 && days.includes('SATURDAY') && days.includes('SUNDAY');
};

export const sortDays = (userSettings, availableDays, longDays = false) => {
    if (userSettings) {
        if (availableDays.length === 7) {
            return i18next.t('common:every_day');
        } else if (onlyWeekdaysSelected(availableDays)) {
            return i18next.t('common:weekdays');
        } else if (onlyWeekendsSelected(availableDays)) {
            return i18next.t('common:weekends');
        } else {
            if (userSettings.userData?.firstDayOfWeek?.dayName === SUNDAY) {
                availableDays.sort((a, b) => daysOfWeekSundayFirst.indexOf(a) - daysOfWeekSundayFirst.indexOf(b));
            } else if (userSettings.userData?.firstDayOfWeek?.dayName === SATURDAY) {
                availableDays.sort((a, b) => daysOfWeekSaturdayFirst.indexOf(a) - daysOfWeekSaturdayFirst.indexOf(b));
            } else {
                availableDays.sort((a, b) => daysOfWeek.indexOf(a) - daysOfWeek.indexOf(b));
            }
            const days = availableDays.map((day) => {
                return i18next.t(`common:${longDays ? DAY_TRANSLATIONS_LONG_MAP[day] : DAY_TRANSLATIONS_MAP[day]}`);
            });
            return days.join(', ');
        }
    } else return;
};

// Represents the plan types that are event based.
export const EVENT_BASED_PLANS = [
    'GarminRunningCoachEventBased',
    'GarminCyclingCoachEventBased',
    'GarminTriathlonCoachEventBased'
];

// Returns true if the adaptive coach plan is event based.
export const isEventBased = (adaptiveCoach) => {
    return EVENT_BASED_PLANS.includes(adaptiveCoach?.trainingSubType?.subTypeKey);
};

// Represents if an AC workout has been completed.
// Note: that COMPLETED_AS_TODAYS_WRKT is a status that is used for workout from another day that has been completed today. These are not necessarily treated the same as COMPLETED_TODAYS_WORKOUT.
export const AC_WORKOUT_STATUSES_COMPLETED = [
    'COMPLETED_TODAYS_WORKOUT',
    'COMPLETED_AS_TODAYS_WRKT',
    'COMPLETED_VIA_ACTIVITY',
    'COMPLETED_VIA_SECONDARY_SPORT',
    'COMPLETED_VIA_PRIMARY_SPORT'
];

export const addDswPlan = (plan) => {
    return gcApi.post('/trainingplan-service/trainingplan/fbt-adaptive', plan);
};

export const makeDswObject = ({
    sport,
    workoutTargetTypes,
    dswPlans,
    availableTrainingDays,
    preferredLongTrainingDays,
    preferredSwimTrainingDays,
    locale,
    planName,
    planDate,
    eventBased,
    selectedStrengthWorkoutType,
    swimAbility
}) => {
    let dswPlan;

    if (sport === Sport.Cycling) {
        dswPlan = dswPlans.find((plan) => {
            return (
                plan.trainingType.typeKey === 'Cycling' &&
                plan.trainingSubType.subTypeKey ===
                    (eventBased ? 'GarminCyclingCoachEventBased' : 'GarminCyclingCoachOpenLoop')
            );
        });
    } else if (sport === Sport.Running) {
        dswPlan = dswPlans.find((plan) => {
            return (
                plan.trainingType.typeKey === 'Running' &&
                plan.trainingSubType.subTypeKey ===
                    (eventBased ? 'GarminRunningCoachEventBased' : 'GarminRunningCoachOpenLoop')
            );
        });
    } else if (sport === Sport.Triathlon) {
        dswPlan = dswPlans.find((plan) => {
            return (
                plan.trainingType.typeKey === 'Triathlon' &&
                plan.trainingSubType.subTypeKey ===
                    (eventBased ? 'GarminTriathlonCoachEventBased' : 'GarminTriathlonCoachOpenLoop')
            );
        });
    }

    let newPlan = {
        templatePlanId: dswPlan.trainingPlanId,
        customPlanName: planName,
        planEndDate: planDate,
        availableTrainingDays: availableTrainingDays,
        preferredLongTrainingDays: preferredLongTrainingDays,
        localeKey: locale,
        workoutTargetTypePrefs: workoutTargetTypes
    };

    if (!!selectedStrengthWorkoutType) {
        newPlan.supplementalSports = [selectedStrengthWorkoutType];
    }

    if (sport === Sport.Triathlon) {
        newPlan.preferredSwimTrainingDays = preferredSwimTrainingDays;
        if (!!swimAbility) {
            newPlan.swimAbility = swimAbility;
        }
    }

    return newPlan;
};

export const isITPTrainingPlan = (event) => {
    return (
        !!event.eventCustomization?.trainingPlanId &&
        event.eventCustomization?.trainingPlanType === EventTrainingPlanTypeIds.ITP
    );
};

export const getTrainingPlan = (trainingPlanId) => {
    return gcApi.get(`/trainingplan-service/trainingplan/phased/${trainingPlanId}`);
};

export const quitTrainingPlan = (trainingPlanId) => {
    return gcApi.delete(`/trainingplan-service/trainingplan/trainingplan/${trainingPlanId}`).catch((e) => {
        // Just ignore a 404
        if (e?.response?.status !== 404) {
            throw e;
        }
    });
};

export const DAY_MAP_SHORT = {
    0: 7,
    1: 1,
    2: 2,
    3: 3,
    4: 4,
    5: 5,
    6: 6
};

export const getAdaptiveTrainingPlanName = (t, plan) => {
    let planType = getRunningType(plan.workoutPlanTypeId);
    let distance;
    switch (planType) {
        case '5K':
            distance = t('training_plans:training_plans_filter_5k');
            break;
        case '10K':
            distance = t('training_plans:training_plans_filter_10K');
            break;
        case 'HALFMARATHON':
            distance = t('training_plans:training_plans_filter_half_marathon');
            break;
    }
    return t('dsw:adaptive_plan_header', { 0: distance, 1: plan.coach.firstName });
};

export const getCoachInfo = (coachContentFulId, locale) => {
    // NOTE: Had to use axios instead of gcApi to get the call to work
    let data =
        '{Coaches(coachKey: "' +
        coachContentFulId +
        '", locale: "' +
        locale +
        '") {fields{firstName, lastName, coachKey}}}';
    return axios.post(
        '/gateway/graph-atp/graphql',
        { query: data },
        {
            headers: {
                'Content-Type': 'application/json'
            },
            baseURL: '/'
        }
    );
};

export const isDSWTrainingPlan = (event) => {
    return (
        !!event.eventCustomization?.trainingPlanId &&
        event.eventCustomization?.trainingPlanType === EventTrainingPlanTypeIds.FBT_ADAPTIVE
    );
};

export const StrengthWorkoutTypes = {
    FullGym: 'STRENGTH_TRAINING_GYM',
    BodyWeight: 'STRENGTH_TRAINING_BODYWEIGHT'
};

// Represents the current supported activity types for DSW. Note that this does not include strength.
export const DSW_SUPPORTED_SPORTS = ['cycling', 'running'];

// Given the tasks for a day, returns true if the day is a rest day.
export const isRestDay = (tasks) => {
    if (!tasks || (tasks?.length === 1 && (tasks[0]?.taskWorkout?.restDay || !tasks[0]?.taskWorkout))) {
        return true;
    } else {
        return false;
    }
};

export const isFullMarathon = (runningDistanceType, runningDistance) => {
    let distanceInMeters = 0;
    if (runningDistance) {
        distanceInMeters = Conversion.convertDistance(
            runningDistance,
            isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile,
            DistanceUnits.meter
        );
    }

    if (runningDistanceType === RunningDistanceTypes.Marathon || isFull(distanceInMeters)) {
        return true;
    } else {
        return false;
    }
};

export const addSportsToPlan = (trainingPlanId, sportsArray) => {
    return gcApi.put(
        `/trainingplan-service/trainingplan/fbt-adaptive/${trainingPlanId}/supplementalSports`,
        sportsArray
    );
};

export const getDSWTrainingPlan = (trainingPlanId) => {
    return gcApi.get(`/trainingplan-service/trainingplan/fbt-adaptive/${trainingPlanId}`);
};

export const hasTrainingStatusCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.trainingStatusCapable;
    });
};

export const hasTrainingReadinessCapableDevice = (devices) => {
    return !!devices?.find((device) => {
        return device.trainingReadinessCapable;
    });
};

export const getDSWVideoMeta = (locale, activityType) => {
    if (activityType === EventActivityTypes.running) {
        return {
            thumbnail: '/images/dsw/garmin_run_coach_thumbnail.png',
            subtitles: `/web-data/dsw/subtitles/${locale}/RunningCoachINTROSubtitles.vtt`,
            video: 'https://connectvideo.garmin.com/coach-v2/GarminRunCoachUpdate.mp4'
        };
    } else if (activityType === EventActivityTypes.cycling) {
        return {
            thumbnail: '/images/dsw/garmin_cycle_coach_thumbnail.png',
            subtitles: `/web-data/dsw/subtitles/${locale}/CyclingCoachINTROSubtitles.vtt`,
            video: 'https://connectvideo.garmin.com/coach-v2/GarminCyclingCoachUpdate.mp4'
        };
    } else if (activityType === EventActivityTypes.triathlon) {
        return {
            thumbnail: '/images/dsw/garmin_tri_coach_thumbnail.jpg',
            subtitles: `/web-data/dsw/subtitles/${locale}/TriathlonCoachINTROSubtitles.vtt`,
            video: 'https://connectvideo.garmin.com/coach-v2/GarminTriCoachUpdate.mp4'
        };
    }
};

export const ATP_ACTIVITIES_METRIC_KEYS = [
    'adaptiveCoachingWorkoutStatus',
    'avgSpeed',
    'activityType',
    'distance',
    'duration',
    'name',
    'startLocal',
    'trainingEffectLabel',
    'workoutComplianceScore',
    'workoutId',
    'workoutType', // filter out workoutTypes that aren't 'ADAPTIVE_COACHING'
    'workoutUuid',
    'aerobicTrainingEffect',
    'parentId',
    'workoutGroupEnumerator'
];

export const STP_ACTIVITIES_METRIC_KEYS = [
    'duration',
    'bmrCalories',
    'calories',
    'totalSets',
    'movingDuration',
    'name',
    'activityType',
    'activeSets',
    'trainingEffectLabel',
    'workoutType',
    'aerobicTrainingEffect',
    'anaerobicTrainingEffect'
];

export const ADAPTIVE_COACHING = 'ADAPTIVE_COACHING';

export const getActivityIconForCalendar = (sportTypeKey, iconSize) => {
    if (sportTypeKey === 'strength_training') {
        return (
            <div
                style={{ fontSize: iconSize, width: iconSize, height: iconSize, lineHeight: iconSize }}
                className={'icon-activity-strength-training'}
            />
        );
    } else if (sportTypeKey === 'multi_sport') {
        return (
            <div
                style={{ fontSize: iconSize, width: iconSize, height: iconSize, lineHeight: iconSize }}
                className={'icon-activity-multisport'}
            />
        );
    }
    return (
        <div
            style={{ fontSize: iconSize, width: iconSize, height: iconSize, lineHeight: iconSize }}
            className={`icon-activity-${sportTypeKey}`}
        />
    );
};

export const defaultEventIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M21.9475 10.1315H19.9528C17.0656 9.39654 15.1759 5.98447 11.7113 4.9871L10.3465 4.72463H8.98163C8.97084 3.94178 7.8547 3.73509 7.5643 4.46216L2 17.3231L3.36483 17.9005L5.2021 13.596H7.24934L7.87927 13.7535C8.81897 14.0223 9.7064 14.4483 10.5039 15.0133C11.3438 15.5908 12.0787 16.2732 12.8136 16.8506C14.231 18.058 15.5958 19.1078 17.3806 19.1078H18.2205L22 10.1839L21.9475 10.1315ZM18.7454 16.1157C17.9833 16.1364 17.2274 15.9744 16.5407 15.6433L15.5958 17.9005C14.7226 17.4458 13.9111 16.8813 13.1811 16.2207L14.126 13.9635C13.3898 13.3098 12.6187 12.6965 11.8163 12.1262L10.8189 14.3834C10.0309 13.8345 9.16345 13.4096 8.24672 13.1236L9.1916 10.8664C8.38927 10.6436 7.55565 10.5549 6.72441 10.6039L7.98425 7.61177C8.81549 7.56277 9.64912 7.65145 10.4514 7.87424L11.5538 5.24956C12.4763 5.52107 13.346 5.94705 14.126 6.50941L13.0236 9.13408C13.8635 9.71151 14.5984 10.3939 15.3333 10.9713L16.4357 8.39917C17.1659 9.04502 17.9777 9.59213 18.8504 10.0265L17.748 12.6511C16.8763 12.194 16.0651 11.6297 15.3333 10.9713L14.0735 13.911C14.8063 14.5681 15.6173 15.1323 16.4882 15.5908L17.748 12.6511C18.434 12.9843 19.1905 13.1463 19.9528 13.1236L18.7454 16.1157ZM11.7638 12.0737L13.0236 9.13408C12.2406 8.57678 11.3718 8.15122 10.4514 7.87424L9.1916 10.8139C10.1126 11.0891 10.9817 11.5148 11.7638 12.0737Z"
            fill="#F27716"
        />
    </svg>
);

export const DAY_JS_MAP = {
    sunday: 0,
    saturday: 6,
    monday: 1
};

export const WEEK = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

export const stayTunedIcon = (
    <svg width="9" height="8" viewBox="0 0 9 8" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="4.83301" cy="4" r="4" fill={ColorDarkuiAccent1} />
    </svg>
);

export const DAY_MAP = {
    0: 'SUNDAY',
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY'
};

export const getActiveTrainingPlans = (limit = 1) => {
    return gcApi.get(`/trainingplan-service/trainingplan/plans?limit=${limit}`);
};

export const isDSW = (plan) => {
    return plan?.trainingPlanCategory === EventTrainingPlanTypeIds.FBT_ADAPTIVE;
};

export const parseCyclingDuration = (duration) => {
    if (duration.indexOf('-') > -1) {
        return +duration.substring(0, duration.indexOf('-'));
    } else {
        return duration;
    }
};

export const parseSwimDistance = (distance) => {
    if (distance.indexOf('-') > -1) {
        return +distance.substring(distance.indexOf('-') + 1);
    } else {
        return distance;
    }
};

export const halfIronManDistanceInMeters = 113120;

export const getTotalLegsInMeters = (completionTargetLegs) => {
    let totalMeters = 0;
    completionTargetLegs.forEach((leg) => {
        if (leg.unitType === 'distance') {
            totalMeters += Conversion.convertDistance(leg.value, leg.unit, DistanceUnits.meter);
        }
    });
    return totalMeters;
};

export const getTriathlonDistance = (triathlonDistance) => {
    let totalMeters = 0;
    const numberParser = new NumberParser({
        numberSeparator: Personalizer.getNumberSeparator()
    });
    Object.keys(triathlonDistance).forEach((key) => {
        if (Number.isFinite(triathlonDistance[key]?.legIndex) && !!triathlonDistance[key]?.value) {
            totalMeters += Conversion.convertDistance(
                numberParser.parse(triathlonDistance[key].value),
                triathlonDistance[key].unit,
                DistanceUnits.meter
            );
        }
    });
    return totalMeters;
};

export const SwimAbility = {
    [SwimTypes.Novice]: {
        min: 600,
        max: 1200
    },
    [SwimTypes.Intermediate]: {
        min: 1000,
        max: 2000
    },
    [SwimTypes.Experienced]: {
        min: 1500,
        max: 2900
    },
    [SwimTypes.Expert]: {
        min: 2000,
        max: 3900
    }
};

export const formatSeconds = (seconds) => {
    return Personalizer.personalizeDuration(seconds, false);
};

export const totalLegTime = (goalLegs) => {
    let time = 0;
    goalLegs.forEach((leg) => {
        if (leg.unitType === 'time') {
            time += leg.value + leg.transitionOutDurationSeconds;
        }
    });
    return time;
};

export const getGoalLegs = (goalLegs) => {
    return goalLegs.map((leg) => {
        return {
            value: leg.value,
            unit: leg.unit,
            legIndex: leg.legIndex,
            transitionOutDurationSeconds: leg.transitionOutDurationSeconds,
            unitType: leg.unitType
        };
    });
};

export const totalLegDistanceInMeters = (goalLegs) => {
    let distance = 0;
    goalLegs.forEach((leg) => {
        if (leg.targetUnitType === 'distance') {
            distance += Conversion.convertDistance(leg.targetValue, leg.targetUnit, DistanceUnits.meter);
        }
    });
    return distance;
};

export const convertDistanceToMetricSystem = (distanceInMeters) => {
    const distance = Conversion.convertDistance(
        distanceInMeters,
        DistanceUnits.meter,
        isMetric() ? DistanceUnits.kilometer : DistanceUnits.mile
    );
    return distance;
};

export const getTriathlonDistanceTargetLegs = (triathlonDistance) => {
    const numberParser = new NumberParser({
        numberSeparator: Personalizer.getNumberSeparator()
    });
    let targetLegs = [];
    Object.keys(triathlonDistance).forEach((key) => {
        if (Number.isFinite(triathlonDistance[key]?.legIndex) && !!triathlonDistance[key]?.value) {
            let leg = triathlonDistance[key];
            targetLegs.push({
                value: numberParser.parse(leg.value),
                unit: leg.unit,
                legIndex: leg.legIndex,
                eventLegActivityType: leg.eventLegActivityType,
                courseId: null,
                unitType: leg.unitType
            });
        }
    });
    targetLegs.sort((a, b) => a.legIndex - b.legIndex);
    return targetLegs;
};

export const getGcDetail = (gcUrl) => {
    return gcApi.get(gcUrl);
};

export const minMaxSwimDistances = {
    meter: {
        min: 300,
        max: 2500
    },
    yard: {
        min: 325,
        max: 2640
    },
    mile: {
        min: 0.18,
        max: 1.5
    },
    kilometer: {
        min: 0.3,
        max: 2.5
    }
};

export const minMaxBikeDistances = {
    meter: {
        min: 7000,
        max: 120000
    },
    yard: {
        min: 7920,
        max: 132000
    },
    mile: {
        min: 4.5,
        max: 75
    },
    kilometer: {
        min: 7,
        max: 120
    }
};

export const minMaxRunDistances = {
    meter: {
        min: 2000,
        max: 32000
    },
    yard: {
        min: 2200,
        max: 35200
    },
    mile: {
        min: 1.25,
        max: 20
    },
    kilometer: {
        min: 2,
        max: 32
    }
};

export const cyclingSpecificPhrase = [
    'AEROBIC_HIGH_SHORTAGE',
    'ANAEROBIC_CAPACITY',
    'PHRASE_ANAEROBIC_SPEED',
    'LACTATE_THRESHOLD',
    'PHRASE_TEMPO',
    'VO2MAX',
    'LONG_WORKOUT'
];
